import React from 'react';
import { useStringsContext } from '../../../../contexts/strings';
import Button from '@mui/material/Button';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import BaseDialog from '../../../common/base-dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, Stack } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import ErrorIcon from '@mui/icons-material/Error';
import useTeamData from '../../../../hooks/useTeamData';
import SceneHeader from '../../../common/scene/scene-header';

function isUrlValid(url: string) {
  const res = url.match(
    /^(https:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
  );
  return res !== null;
}

const Header = ({ teamAbbrv, isReadOnly }: { teamAbbrv: string; isReadOnly: boolean }) => {
  const { strings } = useStringsContext();
  const [value, setValue] = React.useState('');
  const [openUpdateRssLink, setOpenUpdateRssLink] = React.useState(false);
  const [error, setError] = React.useState(false);
  const teamData = useTeamData({ teamAbbrv });

  const newsRssUrl = teamData.query.data?.newsRssUrl || '';
  React.useEffect(() => {
    if (teamData.query.isFetched && newsRssUrl) {
      setValue(newsRssUrl);
    }
  }, [newsRssUrl, teamData.query.isFetched]);
  React.useEffect(() => {
    setError(!isUrlValid(value));
  }, [value]);

  return (
    <>
      <SceneHeader title={strings.menu.news}>
        <Button
          variant="contained"
          startIcon={<RssFeedIcon />}
          onClick={() => {
            setOpenUpdateRssLink(true);
          }}
          disabled={isReadOnly}
        >
          {strings.news.updateRssLinkButton}
        </Button>
      </SceneHeader>
      <BaseDialog
        open={openUpdateRssLink}
        title={newsRssUrl ? strings.news.updateRSSLink : strings.news.addRSSLink}
      >
        <DialogContent>
          <Box sx={{ pt: 7, pb: 2 }}>
            <TextField
              label={strings.news.newsRSSLink}
              value={value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setValue(event.target.value);
              }}
              error={error}
              helperText={
                error ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 1 }}
                    component="span"
                  >
                    <ErrorIcon color="error" />
                    <Box sx={{ pt: 0.5, display: 'block' }} component="span">
                      {strings.news.errorMessageRSSLink}
                    </Box>
                  </Box>
                ) : null
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={(theme) => ({ borderTop: `1px solid ${theme.palette.grey[300]}` })}>
          <Stack direction="row" gap={2} sx={{ justifyContent: 'flex-end', flex: 1 }}>
            <Stack direction="row" gap={2}>
              <Button
                color="primary"
                onClick={() => {
                  setOpenUpdateRssLink(false);
                }}
              >
                {strings.actions.cancel}
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => {
                  // https://www.aikfotboll.se/rss/app
                  teamData.mutation.mutate({
                    newsRssUrl: value,
                  });
                }}
                disabled={newsRssUrl === value || error}
                loading={teamData.query.isLoading || teamData.mutation.isPending}
              >
                {strings.news.publishButton}
              </LoadingButton>
            </Stack>
          </Stack>
        </DialogActions>
      </BaseDialog>
    </>
  );
};

export default Header;
